import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Stack, CircularProgress, Alert,
  Paper,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { chatApi } from "../../api/chat.js";
import { useChat } from "../../chat/useChat.js";
import { ChatWindow } from "../../chat/ChatWindow.js";
import type { PublicBotConfig, ChatWidgetTheme } from "@chatfab/shared";


function InviteGate({ bot, onGranted }: { bot: PublicBotConfig; onGranted: (sid: string) => void }) {
  const [code, setCode]   = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { session_id } = await chatApi.createSession(bot.bot_id, code.trim());
      onGranted(session_id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Invalid code";
      setError(msg === "Too many attempts — try again in 15 minutes" ? msg : "Invalid invite code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#F5F6FA", p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 380, width: "100%", textAlign: "center" }}>
        <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "#EEF0FB", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
          <LockOutlinedIcon sx={{ color: "#5C6BC0" }} />
        </Box>
        <Typography variant="h4" mb={0.5}>{bot.name}</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          This bot requires an invite code to access.
        </Typography>
        <form onSubmit={submit}>
          <Stack spacing={2}>
            <TextField
              label="Invite code" fullWidth autoFocus
              value={code} onChange={e => setCode(e.target.value)}
              error={!!error} helperText={error ?? undefined}
            />
            <Button type="submit" variant="contained" fullWidth disabled={!code.trim() || loading}>
              {loading ? "Verifying…" : "Enter"}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}

function ActiveChat({ bot, sessionId }: { bot: PublicBotConfig; sessionId: string }) {
  const { messages, status, capMessage, sendMessage, sendSuggestedTrigger } = useChat({
    sessionId,
    botId: bot.bot_id,
  });

  // Infer theme from embed_config (the public config only has subset; use defaults)
  const theme: ChatWidgetTheme = {
    primary_color:    bot.ui_config.primary_color ?? "#5C6BC0",
    background_color: "#FFFFFF",
    font_family:      "Inter",
    border_radius:    10,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#F5F6FA" }}>
      <Box sx={{ flex: 1, maxWidth: 720, width: "100%", mx: "auto", my: { xs: 0, sm: 3 }, display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: { xs: 0, sm: 2 }, boxShadow: { xs: 0, sm: 3 } }}>
        <ChatWindow
          messages={messages}
          status={status}
          capMessage={capMessage}
          onSend={sendMessage}
          botName={bot.name}
          welcomeMessage={bot.embed_config.welcome_message}
          theme={theme}
          showBranding={bot.embed_config.show_branding}
          suggestedTriggers={bot.suggested_triggers}
          onSuggestedTrigger={sendSuggestedTrigger}
        />
      </Box>
    </Box>
  );
}

export function ChatPage() {
  const { slug } = useParams<{ slug: string }>();
  const [bot, setBot]             = useState<PublicBotConfig | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    chatApi.resolveBot(slug)
      .then(b => {
        setBot(b);
        // Always create a fresh session on page load
        if (!b.invite_required) {
          chatApi.createSession(b.bot_id).then(r => {
            setSessionId(r.session_id);
          }).catch(() => setError("Failed to start session."));
        }
      })
      .catch(() => setError("Bot not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <CircularProgress />
    </Box>
  );

  if (error || !bot) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <Alert severity="error">{error ?? "Bot not found."}</Alert>
    </Box>
  );

  if (bot.state !== "active") return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <Alert severity="warning">This bot is currently unavailable.</Alert>
    </Box>
  );

  if (bot.invite_required && !sessionId) {
    return <InviteGate bot={bot} onGranted={sid => setSessionId(sid)} />;
  }

  if (!sessionId) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <CircularProgress />
    </Box>
  );

  return <ActiveChat bot={bot} sessionId={sessionId} />;
}
